/** Order statuses — maps to PRD FR-3.1 */
export const OrderStatus = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  SCHEDULED: 'scheduled',
  PICKED_UP: 'picked_up',
  IN_PROGRESS: 'in_progress',
  READY_FOR_DELIVERY: 'ready_for_delivery',
  OUT_FOR_DELIVERY: 'out_for_delivery',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled',
  FAILED: 'failed',
} as const;

export type OrderStatus = (typeof OrderStatus)[keyof typeof OrderStatus];

/** Service types — matches CleanYarn price list categories */
export const ServiceType = {
  DRYCLEANING: 'drycleaning',
  LAUNDRY_SHIRT: 'laundry_shirt',
  LAUNDRY_NATIVE: 'laundry_native',
  LAUNDRY_OTHERS: 'laundry_others',
  BEDDINGS: 'beddings',
  HOUSEHOLD: 'household',
  // Legacy values kept for existing orders in DB
  LADIES: 'ladies',
  MEN: 'men',
  LAUNDRY: 'laundry',
  DRY_CLEANING: 'dry_cleaning',
  PRESS_ONLY: 'press_only',
  ALTERATIONS: 'alterations',
} as const;

export type ServiceType = (typeof ServiceType)[keyof typeof ServiceType];

/** Turnaround times — maps to PRD FR-2.1 */
export const TurnaroundTime = {
  STANDARD: 'standard',       // 72 business hours (excludes Sunday)
  EXPRESS: 'express',          // 48 business hours (excludes Sunday)
  EXPRESS_MAX: 'express_max',  // 24 business hours (excludes Sunday)
  SAME_DAY: 'same_day',       // 8 hours (legacy)
} as const;

export type TurnaroundTime = (typeof TurnaroundTime)[keyof typeof TurnaroundTime];

/** Order source — maps to PRD FR-13.3 */
export const OrderSource = {
  MOBILE_APP: 'mobile_app',
  WEB_PORTAL: 'web_portal',
  POS: 'pos',
  PHONE: 'phone',
  WHATSAPP: 'whatsapp',
} as const;

export type OrderSource = (typeof OrderSource)[keyof typeof OrderSource];

/** Item categories for garments */
export const ItemCategory = {
  SHIRTS: 'shirts',
  TROUSERS: 'trousers',
  SUITS: 'suits',
  DRESSES: 'dresses',
  SKIRTS: 'skirts',
  JACKETS: 'jackets',
  COATS: 'coats',
  BEDDING: 'bedding',
  CURTAINS: 'curtains',
  TOWELS: 'towels',
  UNIFORMS: 'uniforms',
  TRADITIONAL: 'traditional',  // Agbada, Buba, etc.
  DELICATES: 'delicates',
  OTHER: 'other',
} as const;

export type ItemCategory = (typeof ItemCategory)[keyof typeof ItemCategory];

/** Item condition notes — used by driver at pickup (FR-11.3) */
export const ItemCondition = {
  GOOD: 'good',
  STAINED: 'stained',
  TORN: 'torn',
  MISSING_BUTTON: 'missing_button',
  FADED: 'faded',
  OTHER: 'other',
} as const;

export type ItemCondition = (typeof ItemCondition)[keyof typeof ItemCondition];

export interface Order {
  id: string;
  orderNumber: string; // CY-YYYY-NNNNN format
  customerId: string;
  status: OrderStatus;
  serviceType: ServiceType;
  turnaroundTime: TurnaroundTime;
  source: OrderSource;
  pickupAddressId: string;
  deliveryAddressId: string;
  pickupDate: string;
  pickupTimeSlot: string;
  deliveryDate: string;
  deliveryTimeSlot: string;
  subtotal: number;       // Naira, excl. VAT
  vatAmount: number;      // 7.5%
  deliveryFee: number;
  discountAmount: number;
  total: number;
  promoCode?: string;
  notes?: string;
  assignedDriverId?: string;
  locationId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface OrderItem {
  id: string;
  orderId: string;
  category: ItemCategory;
  description?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  specialInstructions?: string;
  condition?: ItemCondition;
  conditionNotes?: string;
  photoUrl?: string;
  barcodeTag?: string;
  /** Item-level status for premium tracking (FR-3.3) */
  itemStatus?: 'pending' | 'washing' | 'drying' | 'ironing' | 'ready';
  /** true if recorded by driver at pickup vs pre-ordered by customer */
  recordedAtPickup: boolean;
  color?: string;
  fabricDamageType?: string;
  brandName?: string;
}

export interface OrderTracking {
  id: string;
  orderId: string;
  status: OrderStatus;
  timestamp: string;
  note?: string;
  actorId: string;
  actorRole: string;
  gpsLatitude?: number;
  gpsLongitude?: number;
}
